<template>
  <div>
    <h1 class="outline--text">Settings</h1>
    <br />

    <v-card>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <v-data-table
        :headers="headers"
        :items="settings"
        :loading="isLoading"
        item-key="key"
        :no-data-text="isLoading ? 'Loading settings...' : 'No settings found'"
        class="elevation-1"
      >
        <template v-slot:item.value="{ item }">
          <span class="text--secondary">{{ preview(item) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small color="blue" title="Edit" @click="openEdit(item)">
            mdi-pencil
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit modal -->
    <v-dialog v-model="dialog" max-width="900" persistent scrollable>
      <v-card v-if="editing">
        <v-card-title><code>{{ editing.key }}</code></v-card-title>

        <v-card-text>
          <v-text-field
            v-model="editDescription"
            label="Description"
            :disabled="isSaving"
            outlined
            dense
          ></v-text-field>

          <v-textarea
            v-if="editing.type === 'textarea'"
            v-model="editText"
            label="Value"
            :disabled="isSaving"
            auto-grow
            rows="16"
            outlined
            :style="editing.is_json ? 'font-family: monospace' : ''"
          ></v-textarea>
          <v-text-field
            v-else
            v-model="editText"
            label="Value"
            :disabled="isSaving"
            outlined
          ></v-text-field>

          <v-alert v-if="formError" type="warning" dense text>
            {{ formError }}
          </v-alert>
          <p v-if="editing.is_json" class="caption grey--text mb-0">
            This value is JSON — edit as valid JSON.
          </p>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="editing.default !== undefined"
            text
            :disabled="isSaving"
            @click="resetToDefault"
          >
            Reset to default
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isSaving" @click="closeEdit">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="isSaving"
            :disabled="!canManage"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSuccess" color="success" timeout="3000">
      Setting saved.
    </v-snackbar>
    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ error }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showError = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  FETCH_SETTINGS,
  UPDATE_SETTING,
} from "@/store/modules/settings/actions";
import { RESOURCE_SETTINGS_MANAGEMENT } from "@/store/modules/admin/getters";
import acl from "@/mixin/acl";

export default {
  name: "SettingsPage",
  mixins: [acl],
  data() {
    return {
      headers: [
        { text: "Key", value: "key" },
        { text: "Description", value: "description", sortable: false },
        { text: "Value", value: "value", sortable: false },
        { text: "", value: "actions", sortable: false, width: 60 },
      ],
      dialog: false,
      editing: null,
      editText: "",
      editDescription: "",
      formError: "",
      showError: false,
      showSuccess: false,
    };
  },
  computed: {
    ...mapGetters("settings", ["settings", "isLoading", "isSaving", "error"]),
    canManage() {
      return this.hasAccess([RESOURCE_SETTINGS_MANAGEMENT]);
    },
  },
  methods: {
    preview(item) {
      const raw = item.is_json
        ? JSON.stringify(item.value)
        : String(item.value);
      return raw.length > 80 ? raw.slice(0, 80) + "…" : raw;
    },
    toText(item) {
      return item.is_json ? JSON.stringify(item.value, null, 2) : item.value;
    },
    openEdit(item) {
      this.editing = item;
      this.editText = this.toText(item);
      this.editDescription = item.description || "";
      this.formError = "";
      this.dialog = true;
    },
    closeEdit() {
      this.dialog = false;
      this.editing = null;
      this.editText = "";
      this.editDescription = "";
      this.formError = "";
    },
    resetToDefault() {
      this.editText = this.editing.is_json
        ? JSON.stringify(this.editing.default, null, 2)
        : this.editing.default;
    },
    save() {
      this.formError = "";
      let value = this.editText;
      if (this.editing.is_json) {
        try {
          value = JSON.parse(this.editText);
        } catch (e) {
          this.formError = "Value must be valid JSON.";
          return;
        }
      }
      this.$store
        .dispatch(`settings/${UPDATE_SETTING}`, {
          key: this.editing.key,
          value,
          description: this.editDescription,
        })
        .then(() => {
          this.showSuccess = true;
          this.closeEdit();
        })
        .catch(() => {
          this.formError = this.error || "Failed to save.";
        });
    },
  },
  mounted() {
    this.$store.dispatch(`settings/${FETCH_SETTINGS}`).catch(() => {
      this.showError = true;
    });
  },
};
</script>
